import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import { lookupsJson } from '@ourbranch/lookups';

import Field from 'core/components/form/form.v2';

const currentYear = new Date().getFullYear();

const yearValidation = Yup.number()
  .required('Year is required')
  .min(1900, 'Year must be 1900 or later')
  .max(currentYear, `Year must be ${currentYear} or earlier`);

// #34019: Gator/Utility Transport Vehicle mirrors ATV (same fields incl. cc, same validation).
const atvLikeInitialValues = {
  name: '',
  year: undefined,
  make: '',
  model: '',
  serialNumber: '',
  cc: undefined,
  description: ''
};

const atvLikeValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  year: yearValidation,
  make: Yup.string().required('Make is required'),
  model: Yup.string().required('Model is required'),
  serialNumber: Yup.string().required('Serial number is required'),
  cc: Yup.mixed().required('CC is required')
});

export const assetInitialValuesByType = {
  atv: atvLikeInitialValues,
  gatorUtilityTransportVehicle: atvLikeInitialValues,
  golfCart: {
    name: '',
    year: undefined,
    make: '',
    model: '',
    serialNumber: '',
    description: ''
  },
  unmannedAircraft: {
    name: '',
    year: undefined,
    make: '',
    model: '',
    serialNumber: '',
    weight: undefined,
    description: ''
  },
  boat: {
    name: '',
    classification: undefined,
    horsepower: undefined,
    year: undefined,
    lengthFt: undefined,
    mph: undefined,
    serialNumber: '',
    description: ''
  }
};

export const assetValidationSchemasByType = {
  atv: atvLikeValidationSchema,
  gatorUtilityTransportVehicle: atvLikeValidationSchema,
  golfCart: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    year: yearValidation,
    make: Yup.string().required('Make is required'),
    model: Yup.string().required('Model is required'),
    serialNumber: Yup.string().required('Serial number is required')
  }),
  unmannedAircraft: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    year: yearValidation,
    make: Yup.string().required('Make is required'),
    model: Yup.string().required('Model is required'),
    serialNumber: Yup.string().required('Serial number is required'),
    weight: Yup.number().required('Weight is required'),
    // GL 3010 prints this description verbatim; carrier requires it so the
    // underwriter sees the actual farming use of the drone. #30737.
    description: Yup.string().required('Description is required')
  }),
  boat: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    classification: Yup.string().required('Classification is required'),
    horsepower: Yup.number().required('Horsepower is required'),
    year: yearValidation,
    lengthFt: Yup.number().required('Length is required'),
    mph: Yup.number().required('Miles per hour is required'),
    serialNumber: Yup.string().required('Serial number is required')
  })
};

const NotOnPolicyAsset = ({ disabled, type: typeProp, pathPrefix = '', onChange, ccOptions = lookupsJson.farm.cc }) => {
  const formikContext = useFormikContext();
  const type = typeProp ?? formikContext?.values?.type;

  if (!type) {
    return null;
  }

  const buildName = (n) => (pathPrefix ? `${pathPrefix}.${n}` : n);

  return (
    <>
      <Field
        xs={12}
        type="string"
        name={buildName('name')}
        mode="dark"
        label="Name*"
        disabled={disabled}
        onChange={onChange}
      />

      {(type === 'atv' || type === 'gatorUtilityTransportVehicle') && (
        <>
          <Field
            xs={4}
            type="numeric"
            name={buildName('year')}
            mode="dark"
            label="Year*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={4}
            type="string"
            name={buildName('make')}
            mode="dark"
            label="Make*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={4}
            type="string"
            name={buildName('model')}
            mode="dark"
            label="Model*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="string"
            name={buildName('serialNumber')}
            mode="dark"
            label="Serial number*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="select"
            name={buildName('cc')}
            mode="dark"
            label="CC*"
            options={ccOptions}
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={12}
            type="textarea"
            name={buildName('description')}
            mode="dark"
            label="Description"
            disabled={disabled}
            onChange={onChange}
          />
        </>
      )}

      {type === 'golfCart' && (
        <>
          <Field
            xs={6}
            type="numeric"
            name={buildName('year')}
            mode="dark"
            label="Year*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="string"
            name={buildName('make')}
            mode="dark"
            label="Make*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="string"
            name={buildName('model')}
            mode="dark"
            label="Model*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="string"
            name={buildName('serialNumber')}
            mode="dark"
            label="Serial number*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={12}
            type="textarea"
            name={buildName('description')}
            mode="dark"
            label="Description"
            disabled={disabled}
            onChange={onChange}
          />
        </>
      )}

      {type === 'unmannedAircraft' && (
        <>
          <Field
            xs={6}
            type="numeric"
            name={buildName('year')}
            mode="dark"
            label="Year*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="string"
            name={buildName('make')}
            mode="dark"
            label="Make*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="string"
            name={buildName('model')}
            mode="dark"
            label="Model*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="string"
            name={buildName('serialNumber')}
            mode="dark"
            label="Serial number*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={12}
            type="numeric"
            name={buildName('weight')}
            mode="dark"
            label="Weight (lbs)*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={12}
            type="textarea"
            name={buildName('description')}
            mode="dark"
            label="Describe the farming operation(s) or use(s) of the unmanned aircraft*"
            disabled={disabled}
            onChange={onChange}
          />
        </>
      )}

      {type === 'boat' && (
        <>
          <Field
            xs={6}
            type="select"
            name={buildName('classification')}
            mode="dark"
            label="Classification*"
            options={lookupsJson.farm.boatClassifications}
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="numeric"
            name={buildName('horsepower')}
            mode="dark"
            label="Horsepower*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="numeric"
            name={buildName('year')}
            mode="dark"
            label="Year*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="numeric"
            name={buildName('lengthFt')}
            mode="dark"
            label="Length (ft)*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="numeric"
            name={buildName('mph')}
            mode="dark"
            label="Miles per hour*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={6}
            type="string"
            name={buildName('serialNumber')}
            mode="dark"
            label="Serial number*"
            disabled={disabled}
            onChange={onChange}
          />
          <Field
            xs={12}
            type="textarea"
            name={buildName('description')}
            mode="dark"
            label="Description"
            disabled={disabled}
            onChange={onChange}
          />
        </>
      )}
    </>
  );
};

NotOnPolicyAsset.propTypes = {
  disabled: PropTypes.bool.isRequired,
  type: PropTypes.string,
  pathPrefix: PropTypes.string,
  onChange: PropTypes.func,
  // State-aware ATV cc options (e.g. MO/IN use a 600cc boundary); defaults to farm.cc.
  ccOptions: PropTypes.arrayOf(PropTypes.object)
};

export default NotOnPolicyAsset;
